// src/features/staff/pages/StaffDetailsPage/components/StaffOverenskomsterTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
} from '@mui/material';
import { Add, Handshake } from '@mui/icons-material';
import { useOverenskomster } from '../../../../tidsregistering/hooks/useOverenskomster';
import { useTidsregistreringOverenskomstPermissions } from '../../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../../hooks/mui/useSnackBar';
import StaffTabSection from './StaffTabSection';

export default function StaffOverenskomsterTab({ staffMember }) {
  const { t } = useTranslation(['staff', 'common']);
  const { showError, showSuccess } = useSnackbar();

  const {
    overenskomster,
    loading,
    fetchForDepartment,
    fetchForStaff,
    assignStaff,
  } = useOverenskomster();
  const { canUpdate: canAssign } = useTidsregistreringOverenskomstPermissions();

  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOverenskomstId, setSelectedOverenskomstId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    if (!staffMember?.staff_member_id) return;
    setLoadingAssignments(true);
    try {
      const [, assignmentsResult] = await Promise.all([
        fetchForDepartment(),
        fetchForStaff(staffMember.staff_member_id),
      ]);
      if (assignmentsResult.success) {
        setAssignments(assignmentsResult.data?.assignments ?? []);
      }
    } finally {
      setLoadingAssignments(false);
    }
  }, [staffMember?.staff_member_id, fetchForDepartment, fetchForStaff]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssign = async () => {
    if (!selectedOverenskomstId || !effectiveFrom) return;
    setSubmitting(true);
    const result = await assignStaff({
      staff_member_id: staffMember.staff_member_id,
      overenskomst_id: selectedOverenskomstId,
      effective_from: effectiveFrom,
    });
    setSubmitting(false);
    if (result.success) {
      showSuccess(t('staff:overenskomster.assigned_success'));
      setDialogOpen(false);
      setSelectedOverenskomstId('');
      loadData();
    } else {
      showError(t('staff:overenskomster.assigned_error'));
    }
  };

  const activeAssignment = assignments.find((a) => !a.effective_to);

  return (
    <StaffTabSection
      icon={Handshake}
      title={t('staff:overenskomster.title')}
      loading={loadingAssignments}
      fill
      subtitle={activeAssignment && (
        <>
          {t('staff:overenskomster.active')}{': '}
          <Chip
            label={overenskomster.find((o) => o.overenskomst_id === activeAssignment.overenskomst_id)?.name ?? activeAssignment.overenskomst_id}
            size="small"
            color="success"
            sx={{ ml: 0.5 }}
          />
        </>
      )}
      actions={canAssign && (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
        >
          {t('staff:overenskomster.assign')}
        </Button>
      )}
    >

      {assignments.length === 0 ? (
        <Alert severity="info">{t('staff:overenskomster.none')}</Alert>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('staff:overenskomster.overenskomst')}</TableCell>
                <TableCell>{t('staff:overenskomster.effective_from')}</TableCell>
                <TableCell>{t('staff:overenskomster.effective_to')}</TableCell>
                <TableCell>{t('staff:overenskomster.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments
                .slice()
                .sort((a, b) => new Date(b.effective_from) - new Date(a.effective_from))
                .map((assignment) => {
                  const overenskomst = overenskomster.find(
                    (o) => o.overenskomst_id === assignment.overenskomst_id
                  );
                  return (
                    <TableRow key={assignment.staff_overenskomst_id}>
                      <TableCell>
                        {overenskomst ? `${overenskomst.name} (${overenskomst.code})` : assignment.overenskomst_id}
                      </TableCell>
                      <TableCell>{assignment.effective_from}</TableCell>
                      <TableCell>{assignment.effective_to ?? '—'}</TableCell>
                      <TableCell>
                        {!assignment.effective_to ? (
                          <Chip label={t('staff:overenskomster.active')} size="small" color="success" />
                        ) : (
                          <Chip label={t('staff:overenskomster.expired')} size="small" color="default" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('staff:overenskomster.assign_dialog_title')}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="staff-overenskomster-tab-overenskomst-label">{t('staff:overenskomster.overenskomst')}</InputLabel>
            <Select labelId="staff-overenskomster-tab-overenskomst-label"
              value={selectedOverenskomstId}
              label={t('staff:overenskomster.overenskomst')}
              onChange={(e) => setSelectedOverenskomstId(e.target.value)}
            >
              {overenskomster.map((o) => (
                <MenuItem key={o.overenskomst_id} value={o.overenskomst_id}>
                  {o.name} ({o.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('staff:overenskomster.effective_from')}
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common:buttons.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedOverenskomstId || !effectiveFrom || submitting}
          >
            {submitting ? t('common:buttons.saving') : t('common:buttons.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </StaffTabSection>
  );
}
